import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// No default Content-Type header here — axios sets "application/json" on its
// own for plain-object bodies, but a hardcoded default here would also get
// sent on FormData (photo/file upload) requests, stripping the multipart
// boundary and making the backend see no "file" part at all (422 "Field
// required"). Let axios/the browser choose the right Content-Type per request.
export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Guaranteed-to-settle timeout backstop: axios's/XHR's own declarative `timeout`
// option is known to never fire on Safari/WebKit for a request caught mid-flight
// across a network interface drop (wifi off/on, sleep/wake) — the promise can
// hang forever. An AbortController timer is spec-guaranteed to abort regardless
// of what the underlying transport is doing. Used for the token-refresh call
// below (a hung refresh would otherwise wedge `refreshPromise` forever, hanging
// every other 401 in the app) and by the offline photo-upload queue.
export function hardTimeout(ms) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return { signal: controller.signal, cancel: () => clearTimeout(timer) };
}

// --- Auto token refresh on 401 -------------------------------------------
// The access_token cookie is short-lived; refresh_token lasts 7 days. When a
// request 401s we transparently call /auth/refresh ONCE (shared across all
// concurrent 401s) to mint a fresh access_token cookie, then retry. This stops
// expired-session 401s from bubbling up as uncaught errors / dev overlays.
// Endpoints that must never trigger a refresh (prevents infinite loops).
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/demo-login", "/auth/demo-users"];

let refreshPromise = null;

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const { response, config } = error;
        if (!response || response.status !== 401 || !config) {
            return Promise.reject(error);
        }
        const url = config.url || "";
        if (config._retried || NO_REFRESH.some((p) => url.includes(p))) {
            return Promise.reject(error);
        }
        config._retried = true;
        try {
            if (!refreshPromise) {
                const { signal, cancel } = hardTimeout(20000);
                refreshPromise = api
                    .post("/auth/refresh", null, { signal })
                    .finally(() => { cancel(); refreshPromise = null; });
            }
            await refreshPromise;     // wait for the single in-flight refresh
            return api(config);       // retry original request with new cookie
        } catch (e) {
            return Promise.reject(error);
        }
    }
);

// Extract the server-provided download filename from a response's
// Content-Disposition header (keeps the Report_Project_ddmmyy convention),
// falling back to `fallback` when the header is absent.
export function filenameFromResponse(res, fallback) {
    const cd = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"] || "";
    // Prefer RFC 5987 filename*=UTF-8''… (keeps unicode), else plain filename=.
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    if (star && star[1]) {
        try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, "")); } catch { /* fall through */ }
    }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    if (plain && plain[1]) return plain[1].trim();
    return fallback;
}

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Polls a background-job status endpoint (returns {status: "running"|"done"|"error", ...})
// every `intervalMs` until it settles, or throws after `maxAttempts`. Used by
// long-running AI/backfill-style jobs that run detached to avoid ingress timeouts.
export async function pollGenerateStatus(url, { maxAttempts = 60, intervalMs = 2000 } = {}) {
    for (let i = 0; i < maxAttempts; i++) {
        const { data } = await api.get(url);
        if (data.status === "done") return data;
        if (data.status === "error") throw new Error(data.error || "Generation failed");
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error("This is taking longer than expected — check back shortly");
}
